import { useQuery } from '@tanstack/react-query';
import { Routes, Route } from 'react-router-dom';
import api from '../api/client';
import { CpcPlanVsIstPage } from './CpcPlanVsIstPage';
import { RequireModuleAdmin } from '../auth/RequireRole';
import { CpcAdminTab } from './CpcAdminTab';

interface CtbHealth {
  status: string;
  service: string;
  version: string;
  timestamp: string;
}

function CtbHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div style={{
      background: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)',
      padding: '1.25rem 1.75rem',
      borderRadius: 'var(--radius-md)',
      marginBottom: '1.25rem',
    }}>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'white', margin: 0 }}>{title}</h1>
      {subtitle && <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)', margin: '0.25rem 0 0' }}>{subtitle}</p>}
    </div>
  );
}

function CtbDashboard() {
  const { data: health } = useQuery<CtbHealth>({
    queryKey: ['ctb', 'health'],
    queryFn: async () => {
      const res = await api.get<CtbHealth>('/ctb/health');
      return res.data;
    },
    staleTime: 30_000,
    retry: false,
  });

  return (
    <div style={{ maxWidth: 960, margin: '0 auto' }}>
      <CtbHeader
        title="Compliance Tool Box"
        subtitle={health ? `Service v${health.version} — ${health.status}` : 'Verbindung wird hergestellt...'}
      />
      <div style={{
        background: 'var(--color-bg-card)', borderRadius: 'var(--radius-md)',
        border: '1px solid var(--color-border)', padding: '2rem',
        textAlign: 'center',
      }}>
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="var(--color-text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '1rem', opacity: 0.5 }}>
          <rect x="6" y="6" width="36" height="36" rx="4" />
          <path d="M6 18h36" />
          <path d="M18 18v24" />
          <path d="M14 12h.01M20 12h.01M26 12h.01" />
        </svg>
        <h2 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--color-text)', marginBottom: '0.5rem' }}>
          Compliance Tool Box
        </h2>
        <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)', maxWidth: 480, margin: '0 auto 1.5rem' }}>
          Dieses Modul befindet sich in der Entwicklung. Hier werden Compliance-Management-Werkzeuge,
          Richtlinien-Verwaltung, Audit-Checklisten und Reporting-Funktionen bereitgestellt.
        </p>
        {health && (
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.375rem 0.75rem', borderRadius: '999px',
            background: health.status === 'ok' ? '#d1fae5' : '#fef2f2',
            color: health.status === 'ok' ? '#065f46' : '#991b1b',
            fontSize: '0.75rem', fontWeight: 600,
          }}>
            <span style={{
              width: 6, height: 6, borderRadius: '50%',
              background: health.status === 'ok' ? '#16a34a' : '#dc2626',
            }} />
            Backend verbunden ({health.service} v{health.version})
          </div>
        )}
      </div>
    </div>
  );
}

function CtbAdmin() {
  return (
    <RequireModuleAdmin module="ctb">
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <CtbHeader title="CTB Administration" subtitle="Konfiguration und Verwaltung der Compliance Tool Box" />
        <CpcAdminTab />
      </div>
    </RequireModuleAdmin>
  );
}

export function CtbModulePage() {
  return (
    <Routes>
      <Route index element={<CtbDashboard />} />
      <Route path="admin" element={<CtbAdmin />} />
      <Route path="reporting" element={<CpcPlanVsIstPage />} />
    </Routes>
  );
}
